import {UIEvent} from "../../u-i-event";
import {TEvent, TEventSubtype} from "services-comun-meteored/modules/services/cmp/web/events";
import AnalyticManager from "../../../analytic/analytic-manager";
import {EAction, ELabel} from "../../../analytic/event-definition";
import {publish} from "../../../util/js-event";
import {UICallback} from "../../u-i-manager";
import {site} from "../../../context/context";
import traduccionGDPR from "cmp-web-translations/modules/traduccion/cmp/gdpr";
import traduccionInsist, {TraduccionCmpInsistWindow} from "cmp-web-translations/modules/traduccion/cmp/insist_window";
import {IIdioma} from "services-comun/modules/net/idiomas";
import {CMPWindow} from "../c-m-p-window";
import {ECMPWindow} from "../interface";
import {ApareceGlobal} from "../../../session/aparece-global";

export enum EInsistWindowEvent {
    ACEPTAR             = 'EV_InsistWindow_Aceptar',
    PLATFORM_APPROVE_S  = 'EV_InsistWindow_PlatformApprove_S',
    PLATFORM_APPROVE_L  = 'EV_InsistWindow_PlatformApprove_L',
    CLOSE               = 'EV_InsistWindow_Close'
}

export interface IInsistWindowAcceptAllEvent {
    callbacks: UICallback[];
}

export interface IInsistWindowCloseEvent {
}

interface ILang {
    title: string;
    data_p1: string;
    platform: string;
    accept: string;
    login: string;
}

interface IOptions {
    allowClose: boolean;
}

export class InsistWindowManager extends CMPWindow {
    /* STATIC */

    private static _INSTANCE: InsistWindowManager;

    public static instance(): InsistWindowManager {
        return this._INSTANCE ??= new InsistWindowManager(ECMPWindow.INSIST);
    }

    /* INSTANCE */
    private _LANG: ILang|undefined;

    private _panel: HTMLElement|undefined;

    private get panel(): HTMLElement {
        if (!this._panel) {
            throw new Error('Panel no inicializado');
        }
        return this._panel;
    }

    /**
     * Abre la ventana de insistencia.
     * @param callbacks Callbacks.
     * @param options Opciones.
     */
    public openInsistWindow(callbacks: UICallback[], options: IOptions = {
        allowClose: true
    }): void {
        this.buildPanel(options.allowClose).then(async container => {

            this._panel = container;

            // Lanza el evento de apertura de GDPR
            const event = UIEvent.create(TEvent.APARECE_INSIST);
            event.register();
            event.notify();

            // Lanzamos evento analítico
            AnalyticManager().register({
                action: EAction.INSIST,
                label: ELabel.APARECE
            });

            if (ApareceGlobal.get().key !== '1') {
                AnalyticManager().register({
                    action: EAction.INSIST,
                    label: ELabel.APARECE_GLOBAL
                });
            }

            // Gestiona el panel
            this.managePanel(options.allowClose, callbacks);
        });
    }

    private async buildPanel(allowClose: boolean): Promise<HTMLElement> {
        const template = await import(/* webpackChunkName: "insistWindow",  */ "../../template/insist-window.pug" + "");

        import((`cmp-web-templates/scss/gdpr.scss`));

        const lang = this.loadI18N();

        const html = template.default({
            logo: site.gdprParams()?.['logo'],
            logo_alt: site.gdprParams()?.['logo_alt'],
            lang,
            login: site.insistModeConfig()?.sae || false,
            allowClose
        });

        const element: HTMLDivElement = document.createElement('div');
        element.innerHTML = html;

        const mainContainer: HTMLElement = <HTMLElement>element.firstElementChild;
        document.body.insertAdjacentElement('beforeend', mainContainer);

        return mainContainer;
    }

    /**
     * Gestiona la ventana.
     * @param allowClose Indica si se permite cerrar la ventana.
     * @param callbacks Callbacks.
     * @private
     */
    private managePanel(allowClose: boolean, callbacks: UICallback[]) : void {

        // Click sobre el botón de aceptar
        const btnAccept: HTMLElement = <HTMLElement>this.panel.querySelector('#btn-insist-accept');
        if (btnAccept) {
            btnAccept.addEventListener('click', async () => {

                publish<IInsistWindowAcceptAllEvent>(EInsistWindowEvent.ACEPTAR, {
                    callbacks
                });
            });
        }

        // Click sobre el botón de login/suscripción
        const btnPlatformApprove: HTMLElement = <HTMLElement>this.panel.querySelector('#btn-insist-platform');
        if (btnPlatformApprove) {
            btnPlatformApprove.addEventListener('click', async () => {
                publish(EInsistWindowEvent.PLATFORM_APPROVE_S, {});
            });
        }

        // Click sobre el link de login
        const btnLogin: HTMLElement = <HTMLElement>this.panel.querySelector('#link-insist-login');
        if (btnLogin) {
            btnLogin.addEventListener('click', async (e) => {
                e.preventDefault();
                publish(EInsistWindowEvent.PLATFORM_APPROVE_L, {});
            });
        }

        // Click sobre el botón de cerrar
        const btnClose: HTMLElement = <HTMLElement>this.panel.querySelector('#close-insist');
        if (btnClose) {
            btnClose.addEventListener('click', async () => {
                if (allowClose) {
                    publish<IInsistWindowCloseEvent>(EInsistWindowEvent.CLOSE, {});
                }
            });
        }
    }

    private loadI18N(): ILang {
        if (!this._LANG) {
            const idioma: IIdioma = {
                idioma: site.language,
                idioma_corto: site.language.split('-')[0],
                defecto: 'en',
                defecto_corto: 'en'
            };

            const langGDPR = traduccionGDPR(idioma);
            const langInsist = traduccionInsist(idioma);

            this._LANG = {
                title: langGDPR.title,
                data_p1: this.getLangValue<TraduccionCmpInsistWindow>(langInsist, langInsist.data_p1, site.insistParams()?.['lang.main']),
                platform: this.getLangValue<TraduccionCmpInsistWindow>(langInsist, langInsist.platform_action_main(site.langKey()), site.insistParams()?.['lang.platform']),
                accept: this.getLangValue<TraduccionCmpInsistWindow>(langInsist, langInsist.change_opinion, site.insistParams()?.['lang.accept']),
                login: langInsist.platform_action_secondary(site.langKey())
            };
        }

        return this._LANG;
    }

    public override close(): void {
        if (this.panel) {
            this.panel.remove();

            // Lanza el evento de cierre de la ventana
            const event = UIEvent.create(TEvent.CIERRA_CMP, TEventSubtype.INSIST_LAYER);
            event.register();
            event.notify();
        }
    }
}
